/**
 * catalogStore — a small reactive cache of each project's tilemap catalog
 * (name, counts, thumbnail), so the `tilemap_ref` node and the catalog panel
 * render map info without every node re-fetching, and so a create/delete in one
 * place updates every consumer. This is the TM3 stand-in for the project-shell
 * catalog the plan folds tilemaps into (doc 04 §3); centralizing it here keeps
 * the graph project store untouched.
 */

import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { tilemapClient } from '../api/tilemapClient';

export interface CatalogMap {
    uuid: string;
    name: string;
    layers_count: number;
    tiles_painted: number;
    thumbnail_uuid: string | null;
    updated_at: string;
}

interface CatalogState {
    byProject: Record<string, CatalogMap[]>;
    loading: Record<string, boolean>;
    refresh: (projectUuid: string) => Promise<void>;
    ensure: (projectUuid: string) => void;
    find: (projectUuid: string, tilemapUuid: string) => CatalogMap | undefined;
}

export const catalogStore = createStore<CatalogState>((set, get) => ({
    byProject: {},
    loading: {},

    refresh: async (projectUuid) => {
        set((s) => ({ loading: { ...s.loading, [projectUuid]: true } }));
        try {
            const maps = await tilemapClient.listTilemaps(projectUuid);
            set((s) => ({
                byProject: { ...s.byProject, [projectUuid]: maps },
                loading: { ...s.loading, [projectUuid]: false },
            }));
        } catch {
            set((s) => ({ loading: { ...s.loading, [projectUuid]: false } }));
        }
    },

    /** Fetch once if not already loaded/loading. */
    ensure: (projectUuid) => {
        const s = get();
        if (s.byProject[projectUuid] || s.loading[projectUuid]) return;
        void s.refresh(projectUuid);
    },

    find: (projectUuid, tilemapUuid) =>
        get().byProject[projectUuid]?.find((m) => m.uuid === tilemapUuid),
}));

export function useCatalog<T>(selector: (s: CatalogState) => T): T {
    return useStore(catalogStore, selector);
}
